/**
 * The locket prints page (sections/mab-locket-prints.liquid).
 *
 * Someone who met us at the booth comes here days later with two photos. The
 * page asks how she is getting her prints ("Locket kit customer" or "Buy
 * photos"), which locket she has, takes two photos placed inside that locket's
 * shape, how many of each photo's three prints are colour and how many black
 * and white, and sends her to checkout: free for a kit customer, who also adds
 * a photo of her booth receipt, and the variant's own price for someone buying
 * photos, who adds no receipt. Up to three lockets ride one submission on
 * either path.
 *
 * Two halves in one file.
 *
 * The top half is pure: which variant a set resolves to, what the button says,
 * the first thing still missing, the properties a cart line carries, and the
 * submission itself (clear old prints lines, one cart add per set, read the
 * cart before any retry, roll back on failure). Every one of them takes what it
 * works on as arguments, fetch included, so 00-docs/checks/locket-prints-page.mjs
 * runs them in Node against a fake cart.
 *
 * The bottom half is the custom element: the booth choice, the tiles, the
 * two crop slots per set with a print choice under each, the receipt (kit
 * only), the gate and the button. The photo
 * machinery itself (file checks, HEIC, decode, the working copy, the resolution
 * guard, the crop maths, the _crop payload, the HEIC resend copy) is the shared
 * core's, mab-photo-core.js, the same functions the product page uses. Nothing
 * here decodes, scales or encodes a photo of its own.
 *
 * The file on each line is the buyer's original, byte for byte: the File the
 * picker produced. The black and white preview is a CSS filter on a preview
 * canvas and nothing else; no grayscale copy is ever made, and nothing that
 * reaches the cart is drawn by a canvas. The one exception is the core's HEIC
 * resend copy, made only after Shopify refuses a HEIC, and the line then says
 * so in _PhotoN_source or _Receipt_source.
 *
 * Nothing touches the DOM, window or customElements at load, so Node can import
 * this file once the two @theme imports are resolved to the asset files.
 */

import {
  HEART_D,
  MIN_CROP_PX,
  buildCrop,
  centredView,
  clampView,
  coverScale,
  cropFromView,
  decode,
  defaultCrop,
  fullResJpeg,
  heicToJpeg,
  isHeicFile,
  isSvgFile,
  isTooSmall,
  matchesNat,
  normaliseRect,
  notPhotoMessage,
  paintView,
  pinchZoom,
  rotateStep,
  rotatedCopy,
  rotatedSize,
  tooSmallMessage,
  wheelZoom,
  workingCopy,
  zoomView,
} from '@theme/mab-photo-core';
import { locketShape } from '@theme/mab-locket-shapes';

/* ==== The pure half ======================================================= */

/** The prints on one line, both photos together: spec criterion 13, exactly. */
export const PRINTS_VALUE = '6';
/** The two photos and their crops. _Photo and _Photo2 keep the product page's names. */
export const PHOTO_NAMES = ['_Photo', '_Photo2'];
export const CROP_NAMES = ['_crop', '_crop2'];
/**
 * How many of each photo's prints are colour and how many black and white
 * (spec criterion 38). Both are sent on every line, 0 included, as strings.
 */
export const COUNT_NAMES = [
  ['_Photo_colour', '_Photo_bw'],
  ['_Photo2_colour', '_Photo2_bw'],
];
/** Written only on a line whose HEIC Shopify refused and that was resent as a JPEG. */
export const SOURCE_NAMES = ['_Photo_source', '_Photo2_source'];
export const RECEIPT_NAME = '_Receipt';
export const RECEIPT_SOURCE_NAME = '_Receipt_source';
/**
 * Which booth path a line came from, so the order and Locket Print Studio can
 * tell them apart without reading the variant (owner change, 4 Oct): 'kit' for
 * "Locket kit customer", 'buy' for "Buy photos". Sent on every line, right
 * after _Prints.
 */
export const BOOTH_CHOICE_NAME = '_Booth_choice';
export const BOOTH_KIT = 'kit';
export const BOOTH_BUY = 'buy';
export const SOURCE_CONVERTED_VALUE = 'heic-converted';
export const PHOTOS_PER_SET = 2;
/** Every photo is printed three times, in whatever mix the buyer chose. */
export const PRINTS_PER_PHOTO = 3;
/**
 * The four print choices under each photo, in the order they are offered, as
 * radio values: the colour count, a hyphen, the black and white count. The
 * section draws them in this order and checks "2-1" (2 colour, 1 black and
 * white) as the default; the markup's checked radio is where the default lives.
 */
export const MIX_VALUES = ['3-0', '2-1', '1-2', '0-3'];
export const MAX_SETS = 3;
/**
 * How long to wait after a 5xx or a lost response before reading the cart.
 * A gateway timeout can come back while Shopify is still storing the files, so
 * the cart is given a moment to show a line that did land before anything is
 * sent a second time.
 */
export const SETTLE_MS = 2500;

/**
 * The booth path for an option value: 'kit' for the kit value (the cheaper
 * one, which Liquid decides by price), 'buy' for any other value, and '' when
 * nothing is chosen yet.
 */
export function boothChoice(booth, kitValue) {
  if (!booth) return '';
  return booth === kitValue ? BOOTH_KIT : BOOTH_BUY;
}

/** JSON from a data attribute, or null rather than a throw. */
export function safeJSON(str) {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch (_) {
    return null;
  }
}

/**
 * One print choice, from its radio value, as { colour, bw }: whole numbers, each
 * 0 to 3, adding up to the three prints a photo gets. Null for anything else,
 * so a value that is not one of the four can never reach a line.
 */
export function parseMix(value) {
  const m = /^(\d)-(\d)$/.exec(String(value == null ? '' : value));
  if (!m) return null;
  const mix = { colour: Number(m[1]), bw: Number(m[2]) };
  return isMix(mix) ? mix : null;
}

/** Whether { colour, bw } is a whole print choice: integers, 0 to 3, adding up to 3. */
export function isMix(mix) {
  if (!mix || typeof mix !== 'object') return false;
  const { colour, bw } = mix;
  return Number.isInteger(colour) && Number.isInteger(bw) && colour >= 0 && bw >= 0 && colour + bw === PRINTS_PER_PHOTO;
}

/**
 * The count a preview's label carries: "1 print", "2 prints", or nothing at 0,
 * where the preview is dimmed and badged "Not printed" instead. `one` and
 * `many` are section settings; [n] in `many` is the count.
 */
export function countLabel(n, one, many) {
  if (!n) return '';
  if (n === 1) return one || '';
  return (many || '').replace('[n]', String(n));
}

/**
 * The Locket values a buyer is offered: one per distinct locket_handle, the
 * first in option order (spec criterion 3). Where two values share a handle,
 * the later one is never offered, which is how "Small Ornate Oval with pearl
 * charm" drops out without anybody typing it: the charm is a product page
 * add-on and never changes which locket a print order is for. A value with no
 * handle is not offered either, because nothing could say what it is.
 *
 *   values    the Locket option's values, in the product's order
 *   variants  [{ options, handle }], from Liquid
 */
export function offeredLockets(values, variants) {
  const seen = new Set();
  const out = [];
  for (const value of values || []) {
    const v = (variants || []).find((x) => x && Array.isArray(x.options) && x.options[1] === value);
    const handle = v && v.handle;
    if (!handle || seen.has(handle)) continue;
    seen.add(handle);
    out.push(value);
  }
  return out;
}

/**
 * The variant for one set: the booth choice is option 1 and the locket is
 * option 2, compared as strings element by element, the way the product page
 * resolves its own variants. Null when the pair has no variant.
 */
export function variantFor(variants, booth, locket) {
  if (!Array.isArray(variants) || !booth || !locket) return null;
  return (
    variants.find((v) => v && Array.isArray(v.options) && v.options[0] === booth && v.options[1] === locket) || null
  );
}

/** The lowest price, in cents, of any variant under one booth choice, or 0 with none. */
export function cheapestFor(variants, booth) {
  const prices = (variants || []).filter((v) => v && v.options && v.options[0] === booth).map((v) => Number(v.price));
  return prices.length ? Math.min(...prices) : 0;
}

/**
 * What the sets add up to, in cents, read from the variants and never typed.
 * A set with no locket picked yet counts at the cheapest variant under the
 * booth choice: every locket-only variant costs the same today, so the button
 * already says the right amount, and the moment a tile is picked it says that
 * variant's own price.
 */
export function totalCents(variants, booth, lockets) {
  const floor = cheapestFor(variants, booth);
  return (lockets || []).reduce((sum, locket) => {
    const v = variantFor(variants, booth, locket);
    return sum + (v ? Number(v.price) : floor);
  }, 0);
}

/**
 * Cents to the shop's money, without the cents when there are none: "$10",
 * not "$10.00", which is how the kickoff writes the button. Currency and
 * locale come from Liquid.
 */
export function formatMoney(cents, currency, locale) {
  const whole = cents % 100 === 0;
  const digits = whole ? 0 : 2;
  try {
    return new Intl.NumberFormat(locale || 'en-CA', {
      style: 'currency',
      currency: currency || 'CAD',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(cents / 100);
  } catch (_) {
    return `$${(cents / 100).toFixed(digits)}`;
  }
}

/**
 * The button: one label for the kit, and the checkout label with the total in
 * place of [price] for a locket bought on its own.
 */
export function buttonLabel({ booth, kitValue, cents, currency, locale, sendLabel, checkoutLabel }) {
  if (!booth || booth === kitValue) return sendLabel || '';
  return (checkoutLabel || '').replace('[price]', formatMoney(cents, currency, locale));
}

/**
 * The first thing still missing, in page order: each set's locket, then its
 * two photos, then the rights box, then the receipt. Null when nothing is.
 *
 *   sets             [{ locket: bool, photos: [bool, bool] }], a photo true
 *                    once cropped and with a whole print choice
 *   rights           bool
 *   receipt          bool, whether a receipt is attached
 *   receiptRequired  false on "Buy photos", where there is no receipt step and
 *                    the receipt is never named; true otherwise
 */
export function firstMissing({ sets, rights, receipt, receiptRequired = true }) {
  for (let i = 0; i < (sets || []).length; i += 1) {
    const set = sets[i];
    if (!set.locket) return { what: 'locket', set: i };
    const slot = (set.photos || []).findIndex((done) => !done);
    if (slot !== -1) return { what: 'photo', set: i, slot };
  }
  if (!rights) return { what: 'rights' };
  if (receiptRequired && !receipt) return { what: 'receipt' };
  return null;
}

/**
 * Which receipts the page takes: JPG, PNG, HEIC and PDF, by type or by name,
 * because a phone does not always say what type a file is.
 */
export function isReceiptFile(file) {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  if (/^image\/(jpeg|jpg|pjpeg|png|heic|heif)$/.test(type) || type === 'application/pdf') return true;
  return /\.(jpe?g|png|heic|heif|pdf)$/.test(name);
}

/**
 * One photo's _crop, as the string that rides the line. Built by the core's
 * buildCrop, the product page's own builder, with the set's registry entry and
 * locket handle, so an unmeasured locket carries no aperture_mm and the Oval
 * Locket carries its 6 x 9 from the registry and from nowhere else.
 *
 * `drawn` is what the crop box was drawn at (shape and on-screen aspect),
 * `nat` the original's upright size, `rotation` the buyer's clockwise turn
 * (0, 90, 180 or 270) and `rect` the kept region as fractions of the photo
 * turned that far (spec criterion 37).
 */
export function cropPayload({ drawn, nat, rect, rotation }, handle) {
  const entry = locketShape(handle);
  return JSON.stringify(buildCrop({ shape: drawn.shape, aspect: drawn.aspect, nat, rect, rotation: rotation || 0 }, entry, handle));
}

/**
 * Every field one cart line carries, as [name, value] pairs in the order they
 * are sent. Spec criteria 13 and 38, and nothing else: no tone, no pair id, no
 * side, no set number.
 *
 *   variantId    from Liquid, never typed
 *   photos       two Files, the originals (or a refused HEIC's resend copy)
 *   crops        two _crop strings
 *   mixes        two { colour, bw }, each whole and adding up to 3; anything
 *                else throws, so no line is ever sent with a wrong count
 *   shape        the locket's mask, 'oval' or 'heart'
 *   boothChoice  'kit' or 'buy', the path at the press; anything else throws
 *   receipt      the receipt File, the same one on every kit line. Kit only: a
 *                kit line without one throws, and on a buy line it is never
 *                sent, whatever is handed in
 *   sources      { photos: [bool x2], receipt: bool }, true only for a resend
 *                copy; the receipt flag counts on a kit line alone
 *
 * The order: id, quantity, _Photo, _crop, _Photo2, _crop2, the four counts,
 * _Prints, _Booth_choice, _locket_shape, _Receipt (kit only),
 * _rights_confirmed, then any resend flags.
 */
export function lineEntries({ variantId, photos, crops, mixes, shape, boothChoice: booth, receipt, sources }) {
  if (booth !== BOOTH_KIT && booth !== BOOTH_BUY) throw new Error(`the booth choice is ${JSON.stringify(booth)}, not kit or buy`);
  const kit = booth === BOOTH_KIT;
  if (kit && !receipt) throw new Error('a kit line has no receipt');
  const entries = [
    ['id', String(variantId)],
    ['quantity', '1'],
  ];
  for (let i = 0; i < PHOTOS_PER_SET; i += 1) {
    entries.push([`properties[${PHOTO_NAMES[i]}]`, photos[i]]);
    entries.push([`properties[${CROP_NAMES[i]}]`, crops[i]]);
  }
  // Both counts for each photo, 0 included, so the studio never has to guess
  // what a missing one means.
  for (let i = 0; i < PHOTOS_PER_SET; i += 1) {
    const mix = mixes && mixes[i];
    if (!isMix(mix)) throw new Error(`photo ${i + 1} has no whole print choice`);
    entries.push([`properties[${COUNT_NAMES[i][0]}]`, String(mix.colour)]);
    entries.push([`properties[${COUNT_NAMES[i][1]}]`, String(mix.bw)]);
  }
  entries.push(['properties[_Prints]', PRINTS_VALUE]);
  entries.push([`properties[${BOOTH_CHOICE_NAME}]`, booth]);
  entries.push(['properties[_locket_shape]', shape]);
  // Owner change, 4 Oct: the receipt belongs to the kit path alone.
  if (kit) entries.push([`properties[${RECEIPT_NAME}]`, receipt]);
  entries.push(['properties[_rights_confirmed]', 'Yes']);
  const src = sources || {};
  for (let i = 0; i < PHOTOS_PER_SET; i += 1) {
    if (src.photos && src.photos[i]) entries.push([`properties[${SOURCE_NAMES[i]}]`, SOURCE_CONVERTED_VALUE]);
  }
  if (kit && src.receipt) entries.push([`properties[${RECEIPT_SOURCE_NAME}]`, SOURCE_CONVERTED_VALUE]);
  return entries;
}

/** The pairs as a multipart body. A file keeps its own name. */
export function toFormData(entries) {
  const body = new FormData();
  for (const [name, value] of entries) {
    if (value && typeof value === 'object' && typeof value.arrayBuffer === 'function') {
      body.append(name, value, value.name || 'file');
    } else {
      body.append(name, value);
    }
  }
  return body;
}

/** The prints lines in a cart, by product handle. */
export function printsLines(cart, productHandle) {
  const items = (cart && Array.isArray(cart.items) && cart.items) || [];
  return items.filter((item) => item && item.handle === productHandle);
}

async function readCart(f, url) {
  try {
    const res = await f(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res || !res.ok) return null;
    return await res.json();
  } catch (_) {
    return null;
  }
}

async function postUpdate(f, url, updates) {
  try {
    const res = await f(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!res || !res.ok) return null;
    return (await res.json().catch(() => null)) || {};
  } catch (_) {
    return null;
  }
}

/**
 * What identifies one set's line in a cart read: its variant id and every
 * property sent as text. The files come back from /cart.js as addresses, so
 * they are left out; the two _crop strings and the print counts are what
 * tell one set from the next. _Booth_choice is text as well, so a kit line and
 * a buy line can never match each other, on top of their variants differing.
 * product-form.js#matchingQuantity matches a line the same way.
 */
export function lineMarks(entries) {
  const marks = [];
  let id = '';
  for (const [name, value] of entries || []) {
    if (name === 'id') id = String(value);
    const prop = /^properties\[(.+)\]$/.exec(name);
    if (prop && typeof value === 'string') marks.push([prop[1], value]);
  }
  return { id, marks };
}

/** Whether a cart line is the one `lineMarks` describes. */
export function matchesMarks(item, want) {
  if (!item || !want || String(item.variant_id) !== want.id) return false;
  const props = item.properties || {};
  return want.marks.every(([name, value]) => String(props[name] ?? '') === value);
}

/**
 * Records one send as landed, and says whether it did.
 *
 * Every send asks for quantity 1, so a send that landed is exactly one unit
 * on one line, and `mine` counts those units per line key. That count, not
 * the line's quantity, is what this submission owns: Shopify merges an add
 * with the same variant and the same properties into the line already there,
 * so a line's quantity can include a timed-out request that landed late, or a
 * second set that carried the same photos. `known` is the quantity last seen
 * on each line, so a line that appeared or grew since the last look can be
 * told apart from one that did not.
 *
 * From an add's own response (`want` null), the line it names is the one that
 * landed. From a cart read after a lost response, only a line carrying this
 * set's variant and this set's properties, new or grown, is this send's. A
 * line that grew without matching is another set's request landing late; it
 * is not credited to this one, and the last look in submitSets takes it off.
 */
function noteLanded(state, items, want) {
  let landed = false;
  for (const item of items) {
    if (!item || !item.key) continue;
    const before = state.known.has(item.key) ? state.known.get(item.key) : 0;
    const now = Number(item.quantity) || 0;
    if (!landed && (want ? now > before && matchesMarks(item, want) : true)) {
      state.mine.set(item.key, (state.mine.get(item.key) || 0) + 1);
      landed = true;
    }
    state.known.set(item.key, now);
  }
  return landed;
}

/**
 * Sends a submission: one line per set, each its own multipart /cart/add.js.
 *
 *   sets           [{ heic: bool, build(converted) -> entries | null }]
 *                  `build(true)` swaps every HEIC for its resend copy and sets
 *                  the source flags; null when that copy could not be made
 *   productHandle  the prints product's handle, from Liquid
 *   urls           { cart, add, update }: /cart.js, /cart/add.js, /cart/update.js
 *   fetch          the browser's fetch, or a fake
 *   toBody         entries to a request body (FormData in the browser)
 *   wait           ms -> promise, so a check does not sit through SETTLE_MS
 *   clearVariantIds  the variants under this submission's booth choice; only
 *                  prints lines on one of them count as stale. Omitted, every
 *                  prints line does.
 *
 * The order, which is spec criterion 12:
 *   1. Any prints line already in the cart is removed, so a buyer back from
 *      checkout does not stack a second submission on the first.
 *   2. Each set goes up on its own, at most three files a request. A 2xx is a
 *      line. A 5xx, a 429 or no response at all might still be a line, so the
 *      cart is read (after a short wait) before anything is sent again: a lost
 *      response is never sent twice. If the cart cannot be read, nothing is
 *      resent, because a blind resend is how a buyer is charged twice. One
 *      retry per set.
 *   3. Any other 4xx is a refusal. With a HEIC on the set it is resent once
 *      with the core's full-resolution JPEG and the source flag (criterion
 *      32); later sets with a HEIC go straight to the copy. Without one it is
 *      final.
 *   4. If any set fails, or anything throws while one is being built or sent,
 *      every prints line this submission added is put back to what it was, so
 *      the cart holds all of the sets or none of them.
 *   5. On success the cart is read once more and anything past what this
 *      submission recorded (a timed-out request that landed late) is taken
 *      off, so three sets never leave more than three lines. After a lost
 *      response the cart is given SETTLE_MS more and read a second time, for
 *      a request that is still landing. A trim that cannot be made is a
 *      failure, and everything comes back off. If, after a lost response,
 *      none of those last reads could be made, the result says so.
 *
 * Resolves to { ok: true }, { ok: true, unverified: true } when the last look
 * after a lost response could not read the cart, or { ok: false, stage, set,
 * rolledBack }, with `error` when something threw.
 */
export async function submitSets({ sets, productHandle, urls, fetch: f, toBody = toFormData, wait, clearVariantIds }) {
  const pause = wait || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  const state = { known: new Map(), mine: new Map(), heicRefused: false, lost: false };

  const before = await readCart(f, urls.cart);
  if (!before) return { ok: false, stage: 'read' };
  // Owner ruling, 4 Oct: a new submission clears only prints lines with the same booth choice.
  // A kit submission and a "Buy photos" submission can sit in one cart
  // together; their lines differ by variant and by _Booth_choice.
  const clearIds = Array.isArray(clearVariantIds) ? clearVariantIds.map(String) : null;
  const stale = printsLines(before, productHandle).filter((line) => !clearIds || clearIds.includes(String(line.variant_id)));
  let base = before;
  if (stale.length) {
    const updates = {};
    for (const line of stale) updates[line.key] = 0;
    const cleared = await postUpdate(f, urls.update, updates);
    if (!cleared) return { ok: false, stage: 'clear' };
    base = cleared;
  }
  // Whatever is still there after the clear is not this submission's.
  for (const line of printsLines(base, productHandle)) state.known.set(line.key, Number(line.quantity) || 0);
  const outside = new Map(state.known);

  // `want` is the set's own variant and properties: only a line carrying
  // them is this set's (BLOCKING 1 in the 4 Oct review).
  const landedInCart = async (want) => {
    state.lost = true;
    await pause(SETTLE_MS);
    let cart = await readCart(f, urls.cart);
    if (!cart) cart = await readCart(f, urls.cart);
    if (!cart) return null;
    return noteLanded(state, printsLines(cart, productHandle), want);
  };

  const sendSet = async (set) => {
    let converted = !!(state.heicRefused && set.heic);
    let retried = false;
    // At most four sends: the original, one retry, the resend copy, and its retry.
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const entries = await set.build(converted);
      if (!entries) return false;
      const want = lineMarks(entries);
      let res = null;
      try {
        res = await f(urls.add, { method: 'POST', headers: { Accept: 'application/json' }, body: toBody(entries) });
      } catch (_) {
        res = null;
      }
      const status = res ? Number(res.status) || 0 : 0;
      if (status >= 200 && status < 300) {
        const json = await res.json().catch(() => null);
        const items = json ? (Array.isArray(json.items) ? json.items : [json]) : [];
        if (noteLanded(state, items, null)) return true;
        return (await landedInCart(want)) === true;
      }
      // A 429 is Shopify asking for a moment, not a refusal: it is treated
      // like a 5xx, so it never sets off the HEIC resend or a rollback on
      // first sight.
      if (status === 0 || status === 429 || status >= 500) {
        const landed = await landedInCart(want);
        if (landed === true) return true;
        if (landed === null || retried) return false;
        retried = true;
        continue;
      }
      if (status >= 400 && status < 500 && set.heic && !converted) {
        converted = true;
        state.heicRefused = true;
        retried = false;
        continue;
      }
      return false;
    }
    return false;
  };

  // Every prints line back to what was there before this submission, which
  // after the clear is nothing: the lines this submission recorded, and any
  // that landed without a word. When the cart cannot be read, the recorded
  // lines at least.
  const rollback = async () => {
    const updates = {};
    for (const key of state.mine.keys()) updates[key] = outside.get(key) || 0;
    const cart = await readCart(f, urls.cart);
    if (cart) {
      for (const line of printsLines(cart, productHandle)) {
        const want = outside.get(line.key) || 0;
        if ((Number(line.quantity) || 0) > want) updates[line.key] = want;
      }
    }
    if (!Object.keys(updates).length) return true;
    for (let i = 0; i < 2; i += 1) {
      if (await postUpdate(f, urls.update, updates)) return true;
    }
    return false;
  };

  // A throw anywhere in here (a set's build, a HEIC library that would not
  // load, a crop that cannot be built) still rolls back what was added.
  let at = 0;
  try {
    for (at = 0; at < sets.length; at += 1) {
      if (!(await sendSet(sets[at]))) {
        return { ok: false, stage: 'add', set: at, rolledBack: await rollback() };
      }
    }

    // The last look: a request that timed out and landed after its retry
    // shows up here as a line nobody recorded, or as a line grown past the
    // units this submission sent to it. Each line is held to what was there
    // before plus those units, so three sets never leave more than three.
    // After a lost response a request can still be on its way, so the cart
    // is given another SETTLE_MS and looked at twice.
    const looks = state.lost ? 2 : 1;
    let looked = false;
    for (let look = 0; look < looks; look += 1) {
      if (state.lost) await pause(SETTLE_MS);
      let after = await readCart(f, urls.cart);
      if (!after) after = await readCart(f, urls.cart);
      if (!after) continue;
      looked = true;
      const trim = {};
      for (const line of printsLines(after, productHandle)) {
        const want = (outside.get(line.key) || 0) + (state.mine.get(line.key) || 0);
        if ((Number(line.quantity) || 0) > want) trim[line.key] = want;
      }
      if (Object.keys(trim).length && !(await postUpdate(f, urls.update, trim))) {
        return { ok: false, stage: 'trim', rolledBack: await rollback() };
      }
    }
    // After a lost response, a cart that could not be read at the end may
    // still hold a late landing nobody could take off. Every set is in, so
    // the send stands, but the page shows the buyer her cart before she pays.
    if (state.lost && !looked) return { ok: true, unverified: true };
  } catch (error) {
    let rolledBack = false;
    try {
      rolledBack = await rollback();
    } catch (_) {
      rolledBack = false;
    }
    // The page logs it; the check reads it.
    return { ok: false, stage: 'add', set: at, rolledBack, error };
  }
  return { ok: true };
}

/**
 * The outline a tile shows when its locket's photo does not resolve, and the
 * gold ring drawn around a crop box: the registry's shape, drawn from the
 * core's own heart path, never a seal. SVG markup, aria-hidden by the caller.
 */
export function shapeSvg(shape) {
  if (shape === 'heart') {
    return `<svg viewBox="0 0 100 100" preserveAspectRatio="none" focusable="false" aria-hidden="true"><path d="${HEART_D}" fill="none" stroke="currentColor" stroke-width="1.5" vector-effect="non-scaling-stroke"/></svg>`;
  }
  if (shape === 'oval') {
    return '<svg viewBox="0 0 100 100" preserveAspectRatio="none" focusable="false" aria-hidden="true"><ellipse cx="50" cy="50" rx="49" ry="49" fill="none" stroke="currentColor" stroke-width="1.5" vector-effect="non-scaling-stroke"/></svg>';
  }
  return '';
}

/**
 * The shape for a locket: the registry first, by handle. The locket product's
 * own locket_shape metafield, which Liquid hands over, only for a handle the
 * registry does not know. Null when neither says.
 */
export function shapeFor(handle, fallback) {
  const entry = locketShape(handle);
  if (entry) return entry.shape;
  return fallback === 'oval' || fallback === 'heart' ? fallback : null;
}

/* ==== The custom element ================================================== */

const Base = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

/**
 * The on-screen box for a locket. The registry's aspect for a locket it knows.
 * For one it does not, the product page's own fallback ratios (a 3 by 4 oval, a
 * square heart), which are screen ratios and never a size.
 */
function drawnFor(handle, fallbackShape) {
  const entry = locketShape(handle);
  if (entry) return { shape: entry.shape, aspect: entry.aspect };
  const shape = shapeFor(handle, fallbackShape);
  if (!shape) return null;
  return { shape, aspect: shape === 'heart' ? 1 : 0.75 };
}

function release(bitmap) {
  if (bitmap && typeof bitmap.close === 'function') bitmap.close();
}

function blankCanvas(canvas) {
  if (!canvas) return;
  canvas.width = 0;
  canvas.height = 0;
}

class MabLocketPrints extends Base {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    const d = this.dataset;
    this.productHandle = d.productHandle || '';
    this.variants = safeJSON(d.variants) || [];
    this.boothValues = safeJSON(d.boothValues) || [];
    // The six lockets on offer, worked out from the data the same way Liquid
    // drew the tiles. A value outside this list is never selected or sent.
    this.offered = offeredLockets(safeJSON(d.locketValues) || [], this.variants);
    this.kitValue = d.kitValue || '';
    this.booth = '';
    this.receipt = null;
    this.minPx = MIN_CROP_PX;
    this.urls = {
      cart: `${d.cartUrl || '/cart'}.js`,
      add: `${d.cartAddUrl || '/cart/add'}.js`,
      update: `${d.cartUpdateUrl || '/cart/update'}.js`,
    };

    this.rest = this.querySelector('[data-rest]');
    this.boothGroup = this.querySelector('[data-booth] [data-choice-group]');
    this.boothLines = Array.from(this.querySelectorAll('[data-booth-line]'));
    this.addSetBtn = this.querySelector('[data-act="add-set"]');
    this.rights = this.querySelector('[data-rights] input[type="checkbox"]');
    this.receiptInput = this.querySelector('[data-receipt-input]');
    this.receiptBox = this.querySelector('[data-receipt]');
    this.receiptName = this.querySelector('[data-receipt-name]');
    this.receiptError = this.querySelector('[data-receipt-error]');
    this.submitBtn = this.querySelector('[data-act="submit"]');
    this.helper = this.querySelector('[data-helper]');
    this.sendError = this.querySelector('[data-send-error]');
    this.sendStatus = this.querySelector('[data-send-status]');
    this.chooseFirst = this.querySelector('[data-choose-first]');

    this.slotByEl = new WeakMap();
    this.setByEl = new WeakMap();
    this.sets = Array.from(this.querySelectorAll('[data-set]')).map((el) => this.makeSet(el));
    this.active = this.sets.length ? [this.sets[0]] : [];

    this.installHeartClip();
    this.fillOutlines();
    this.setupBooth();
    this.sets.forEach((set) => this.setupSet(set));

    this._onClick = (event) => this.onClick(event);
    this.addEventListener('click', this._onClick);
    if (this.rights) this.rights.addEventListener('change', () => this.updateGate());
    if (this.receiptInput) this.receiptInput.addEventListener('change', () => this.onReceipt());

    this._resizeTimer = 0;
    this._onResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.redrawAll(), 150);
    };
    window.addEventListener('resize', this._onResize);
    this._onPageShow = (event) => this.onPageShow(event);
    window.addEventListener('pageshow', this._onPageShow);

    this.syncSets();
    this.syncReceiptStep();
    this.updateGate();
  }

  disconnectedCallback() {
    if (this._onResize) {
      clearTimeout(this._resizeTimer);
      window.removeEventListener('resize', this._onResize);
    }
    if (this._onPageShow) window.removeEventListener('pageshow', this._onPageShow);
    (this.sets || []).forEach((set) => set.slots.forEach((slot) => this.clearSlot(slot)));
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---- shapes ------------------------------------------------------------- */

  /**
   * The heart mask is a clip path drawn from the core's HEART_D, put in the
   * page once, so the mask, the ring and the composite on the product page are
   * one outline. The oval is a CSS ellipse and needs none.
   */
  installHeartClip() {
    const id = `${this.id || 'mab-prints'}-heart`;
    if (!document.getElementById(id)) {
      const holder = document.createElement('div');
      holder.innerHTML = `<svg class="mab-prints__defs" width="0" height="0" aria-hidden="true" focusable="false"><defs><clipPath id="${id}" clipPathUnits="objectBoundingBox"><path transform="scale(0.01)" d="${HEART_D}"/></clipPath></defs></svg>`;
      this.prepend(holder.firstChild);
    }
    this.style.setProperty('--mab-prints-heart-clip', `url('#${id}')`);
  }

  /** A tile with no photo shows its locket's outline from the registry. */
  fillOutlines() {
    this.querySelectorAll('[data-outline]').forEach((el) => {
      const input = el.closest('label') && el.closest('label').querySelector('input');
      const shape = input ? shapeFor(input.dataset.handle, input.dataset.shape) : null;
      el.innerHTML = shapeSvg(shape);
    });
  }

  /* ---- the booth choice --------------------------------------------------- */

  chips() {
    return this.boothGroup ? Array.from(this.boothGroup.querySelectorAll('[data-choice-value]')) : [];
  }

  setupBooth() {
    if (!this.boothGroup) return;
    const chips = this.chips();
    // One tab stop for the group. The choice row puts it on the chosen chip,
    // and here nothing is chosen yet, so it goes on the first one. Done here
    // rather than in the shared snippet, which the product page also renders
    // and whose markup its checks pin.
    if (chips.length && !chips.some((c) => c.tabIndex === 0)) chips[0].tabIndex = 0;
    this.boothGroup.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-choice-value]');
      if (chip && this.boothGroup.contains(chip)) this.chooseBooth(chip);
    });
    this.boothGroup.addEventListener('keydown', (event) => {
      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      const all = this.chips();
      const here = event.target.closest ? event.target.closest('[data-choice-value]') : null;
      const from = Math.max(0, all.indexOf(here));
      let next = from;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = all.length - 1;
      else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (from + 1) % all.length;
      else next = (from - 1 + all.length) % all.length;
      event.preventDefault();
      // A radio group chooses as the arrows move, which is what role="radio"
      // promises. Choosing here costs nothing: the choice can be changed at
      // any time and nothing below is reset by it.
      this.chooseBooth(all[next]);
      all[next].focus({ preventScroll: true });
    });
  }

  chooseBooth(chip) {
    const chips = this.chips();
    const index = chips.indexOf(chip);
    const value = this.boothValues[index];
    if (!value) return;
    this.booth = value;
    chips.forEach((c) => {
      const on = c === chip;
      c.setAttribute('aria-checked', on ? 'true' : 'false');
      c.tabIndex = on ? 0 : -1;
    });
    const label = this.boothGroup.querySelector('[data-choice-value-label]');
    if (label) label.textContent = chip.dataset.choiceValue;
    const kind = this.boothKind();
    this.boothLines.forEach((line) => {
      line.hidden = line.dataset.boothLine !== kind;
    });
    // The receipt step follows the choice, both ways, every time it changes.
    // Any receipt error is from the other visit and is cleared.
    this.showReceiptError('');
    this.syncReceiptStep();
    if (this.rest && this.rest.hasAttribute('inert')) {
      this.rest.removeAttribute('inert');
      this.rest.removeAttribute('aria-disabled');
      this.rest.dataset.open = 'true';
    }
    if (this.chooseFirst) this.chooseFirst.hidden = true;
    this.updateGate();
  }

  /** 'kit', 'buy', or '' before a choice. */
  boothKind() {
    return boothChoice(this.booth, this.kitValue);
  }

  /**
   * The receipt step is the kit path's alone (owner change, 4 Oct). It ships
   * hidden and inert, and only "Locket kit customer" shows it: on "Buy photos"
   * it is hidden and inert again, so nothing in it can take focus or be read
   * out, the gate does not ask for it and no line carries it.
   *
   * Switching restores, it does not reset: a receipt chosen on the kit path
   * stays in memory while she is on "Buy photos", unseen and unsent, and is
   * there as it was (the same file, the same name shown) when she switches
   * back. Remove is the only thing that drops it.
   *
   * While a submission sends, the step stays inert whatever the path, and
   * setSending calls this again when it lets go, so a failed buy send never
   * leaves the hidden step operable.
   */
  syncReceiptStep() {
    const box = this.receiptBox;
    if (!box) return;
    const show = this.boothKind() === BOOTH_KIT;
    box.hidden = !show;
    box.toggleAttribute('inert', !show || !!this._sending);
  }

  /* ---- sets --------------------------------------------------------------- */

  makeSet(el) {
    const set = {
      el,
      title: el.querySelector('[data-set-title]'),
      removeBtn: el.querySelector('[data-act="remove-set"]'),
      tiles: Array.from(el.querySelectorAll('[data-tile]')),
      locket: '',
      handle: '',
      drawn: null,
      shape: '',
      slots: [],
    };
    set.slots = Array.from(el.querySelectorAll('[data-slot]')).map((slotEl, i) => this.makeSlot(slotEl, i, set));
    this.setByEl.set(el, set);
    return set;
  }

  makeSlot(el, index, set) {
    const slot = {
      el,
      index,
      set,
      input: el.querySelector('[data-file]'),
      pickBtn: el.querySelector('[data-act="pick"]'),
      applyBtn: el.querySelector('[data-act="apply"]'),
      viewport: el.querySelector('[data-viewport]'),
      bleed: el.querySelector('[data-canvas="bleed"]'),
      crop: el.querySelector('[data-canvas="crop"]'),
      colour: el.querySelector('[data-canvas="colour"]'),
      grey: el.querySelector('[data-canvas="bw"]'),
      zoom: el.querySelector('[data-zoom]'),
      error: el.querySelector('[data-slot-error]'),
      status: el.querySelector('[data-slot-status]'),
      // The print choice under the pair: four native radios. They live in
      // the page, so the choice outlasts a turn, an adjust and a replace.
      mixInputs: Array.from(el.querySelectorAll('[data-mix]')),
      // The two previews, each with its count and its "Not printed" badge.
      prints: {
        colour: el.querySelector('[data-print="colour"]'),
        bw: el.querySelector('[data-print="bw"]'),
      },
      state: 'empty',
      original: null,
      converted: null,
      // The working copy, upright, and the one the crop box draws: the same
      // bitmap at rotation 0, a turned copy of it otherwise.
      upright: null,
      bitmap: null,
      rotation: 0,
      nat: null,
      view: { scale: 0, x: 0, y: 0 },
      minScale: 1,
      rect: null,
      pixels: null,
      drawn: null,
      pinchBase: null,
      busy: false,
      // True while the status line holds the rotate note.
      note: false,
    };
    this.slotByEl.set(el, slot);
    return slot;
  }

  setupSet(set) {
    set.tiles.forEach((input) => {
      input.addEventListener('change', () => {
        if (input.checked) this.chooseLocket(set, input);
      });
    });
    set.slots.forEach((slot) => this.setupSlot(slot));
  }

  setupSlot(slot) {
    if (slot.input) slot.input.addEventListener('change', () => this.onPick(slot));
    if (slot.zoom) {
      slot.zoom.addEventListener('input', () => {
        this.clearNote(slot);
        this.onZoom(slot);
      });
    }
    if (slot.viewport) this.setupPointer(slot);
    slot.mixInputs.forEach((input) => {
      input.addEventListener('change', () => {
        // "Photo 2 turned" lasts until the next thing done to that photo.
        this.clearNote(slot);
        this.paintMix(slot);
        this.updateGate();
      });
    });
    this.paintMix(slot);
  }

  /* ---- the print choice ---------------------------------------------------- */

  /** The slot's print choice as { colour, bw }, or null with none chosen. */
  mixFor(slot) {
    const on = (slot.mixInputs || []).find((input) => input.checked);
    return on ? parseMix(on.value) : null;
  }

  /** Back to the choice the page opened with, the radio the markup checks. */
  resetMix(slot) {
    (slot.mixInputs || []).forEach((input) => {
      input.checked = input.defaultChecked;
    });
    this.paintMix(slot);
  }

  /**
   * Each preview says how many of it are printed. At 0 the picture dims and a
   * "Not printed" badge in words sits on it; the label under it stays at full
   * strength. The CSS dims on data-printed, never the label.
   */
  paintMix(slot) {
    const mix = this.mixFor(slot);
    const d = this.dataset;
    for (const kind of ['colour', 'bw']) {
      const fig = slot.prints && slot.prints[kind];
      if (!fig) continue;
      const n = mix ? mix[kind] : null;
      fig.dataset.printed = n === 0 ? 'false' : 'true';
      const badge = fig.querySelector('[data-not-printed]');
      if (badge) badge.hidden = n !== 0;
      const count = fig.querySelector('[data-print-count]');
      if (count) {
        const text = n ? countLabel(n, d.countSingle, d.countPlural) : '';
        count.textContent = text ? `, ${text}` : '';
      }
    }
  }

  chooseLocket(set, input) {
    if (!this.offered.includes(input.value)) {
      input.checked = false;
      return;
    }
    const drawn = drawnFor(input.dataset.handle, input.dataset.shape);
    set.locket = input.value;
    set.handle = input.dataset.handle || '';
    set.drawn = drawn;
    set.shape = drawn ? drawn.shape : '';
    set.el.dataset.shape = set.shape;
    set.slots.forEach((slot) => this.applySetShape(slot));
    this.updateGate();
  }

  /**
   * A slot follows its set's locket. A crop drawn for another shape or another
   * box no longer fits, so it goes back to the crop box with the same photo,
   * and the set is not ready again until it is placed. A crop that still fits
   * stays: its _crop is built at send time with whichever locket the set has.
   */
  applySetShape(slot) {
    // "Pick your locket first" and a too-small crop drawn for the last locket
    // both stop being true when the locket changes.
    this.hideSlotError(slot);
    const drawn = slot.set.drawn;
    slot.el.dataset.shape = drawn ? drawn.shape : '';
    // The crop box and both prints are drawn at the registry's on-screen ratio
    // for this locket. A screen ratio, never a size.
    if (drawn) slot.el.style.setProperty('--mab-prints-aspect', String(drawn.aspect));
    else slot.el.style.removeProperty('--mab-prints-aspect');
    slot.el.querySelectorAll('[data-ring]').forEach((ring) => {
      ring.innerHTML = shapeSvg(drawn ? drawn.shape : '');
    });
    if (slot.state === 'done' && drawn && slot.drawn && (slot.drawn.shape !== drawn.shape || slot.drawn.aspect !== drawn.aspect)) {
      this.startCrop(slot);
    } else if (slot.state === 'cropping') {
      this.startCrop(slot);
    }
  }

  syncSets() {
    const many = this.active.length > 1;
    this.sets.forEach((set) => {
      const at = this.active.indexOf(set);
      set.el.hidden = at === -1;
      if (set.title) {
        // Always there, so the heading order runs h1, h2, h3 and the region
        // has a name: "Your locket" for a screen reader alone when there is
        // one, "Locket 2" on the screen when there are more.
        set.title.classList.toggle('visually-hidden', !many);
        set.title.textContent = many ? (this.dataset.setLabel || '').replace('[n]', String(at + 1)) : this.dataset.setLabelSingle || '';
      }
      if (set.removeBtn) set.removeBtn.hidden = at < 1;
    });
    if (this.addSetBtn) this.addSetBtn.hidden = this.active.length >= Math.min(MAX_SETS, this.sets.length);
  }

  addSet() {
    const next = this.sets.find((set) => !this.active.includes(set));
    if (!next || this.active.length >= MAX_SETS) return;
    this.resetSet(next);
    const last = this.active[this.active.length - 1];
    last.el.after(next.el);
    this.active.push(next);
    this.syncSets();
    this.updateGate();
    if (next.title) {
      next.title.tabIndex = -1;
      next.title.focus({ preventScroll: true });
    }
    next.el.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  removeSet(set) {
    const at = this.active.indexOf(set);
    if (at < 1) return;
    this.active.splice(at, 1);
    this.resetSet(set);
    this.syncSets();
    this.updateGate();
    const before = this.active[at - 1];
    const target = (this.addSetBtn && !this.addSetBtn.hidden && this.addSetBtn) || (before && before.title) || null;
    if (target) {
      if (target === (before && before.title)) target.tabIndex = -1;
      target.focus({ preventScroll: false });
    }
  }

  resetSet(set) {
    set.tiles.forEach((input) => {
      input.checked = false;
    });
    set.locket = '';
    set.handle = '';
    set.drawn = null;
    set.shape = '';
    set.el.dataset.shape = '';
    set.slots.forEach((slot) => {
      this.clearSlot(slot);
      this.resetMix(slot);
      this.applySetShape(slot);
    });
  }

  /* ---- clicks -------------------------------------------------------------- */

  onClick(event) {
    const btn = event.target.closest('[data-act]');
    if (!btn || !this.contains(btn)) return;
    const act = btn.dataset.act;
    const slotEl = btn.closest('[data-slot]');
    const slot = slotEl ? this.slotByEl.get(slotEl) : null;
    const setEl = btn.closest('[data-set]');
    const set = setEl ? this.setByEl.get(setEl) : null;
    // "Photo 2 turned" lasts until the next thing done to that photo.
    if (slot) this.clearNote(slot);
    switch (act) {
      case 'pick':
      case 'replace':
        if (slot) this.openPicker(slot);
        break;
      case 'apply':
        if (slot) this.applyCrop(slot);
        break;
      case 'rotate-left':
      case 'rotate-right':
        if (slot) this.rotate(slot, act === 'rotate-left' ? 'left' : 'right');
        break;
      case 'adjust':
        if (slot) this.startCrop(slot, { keep: true });
        break;
      case 'remove':
        if (slot) {
          // Remove starts the photo over, print choice included. Replace
          // (handleFile) clears the photo alone and keeps the choice.
          this.clearSlot(slot);
          this.resetMix(slot);
          this.updateGate();
          if (slot.pickBtn) slot.pickBtn.focus();
        }
        break;
      case 'add-set':
        this.addSet();
        break;
      case 'remove-set':
        if (set) this.removeSet(set);
        break;
      case 'receipt-pick':
        if (this.receiptInput) this.receiptInput.click();
        break;
      case 'receipt-remove': {
        this.setReceipt(null);
        const pick = this.querySelector('.mab-prints__receipt-pick');
        if (pick) pick.focus();
        break;
      }
      case 'submit':
        this.onSubmit();
        break;
      default:
    }
  }

  /* ---- a photo slot -------------------------------------------------------- */

  openPicker(slot) {
    // The crop box needs the locket's shape before it can draw, so a photo
    // waits for a tile, and the slot says so rather than doing nothing.
    if (!slot.set.drawn) {
      this.showSlotError(slot, this.dataset.msgNeedLocket);
      this.focusFirstTile(slot.set);
      return;
    }
    if (slot.input) slot.input.click();
  }

  onPick(slot) {
    const file = slot.input.files && slot.input.files[0];
    // The picker is emptied straight away, so choosing the same file again
    // still fires a change. The File is held on the slot, unchanged.
    slot.input.value = '';
    if (file) this.handleFile(slot, file);
  }

  /**
   * The product page's handleFile, for one slot: the same refusals in the same
   * order, through the same core functions. SVG is turned away before the
   * decode, the decode decides whether this is a photo, HEIC gets the vendored
   * converter, the guard judges the original's pixels, and the slot keeps the
   * picked File itself as what goes on the order.
   */
  async handleFile(slot, file) {
    this.hideSlotError(slot);
    if (isSvgFile(file)) {
      this.showSlotError(slot, this.dataset.msgUnsupported);
      return;
    }
    slot.busy = true;
    let bmp = null;
    try {
      bmp = await decode(file);
      if (!bmp && isHeicFile(file)) {
        this.setSlotStatus(slot, this.dataset.msgConverting);
        const jpeg = await heicToJpeg(file, this.dataset.heicLib);
        bmp = await decode(jpeg);
      }
      if (!bmp) {
        this.showSlotError(slot, notPhotoMessage(file, this.dataset.msgUnsupported, this.dataset.msgDecodeFailed));
        return;
      }
      if (isTooSmall(bmp.width, bmp.height, this.minPx)) {
        // The size is read before the bitmap is let go: a closed bitmap
        // reports 0 by 0.
        const message = this.tooSmall(bmp.width, bmp.height);
        release(bmp);
        this.showSlotError(slot, message);
        return;
      }
      const work = await workingCopy(bmp);
      // Only now, with a replacement that opened and is big enough, is the
      // photo it replaces let go.
      this.clearSlot(slot);
      slot.original = file;
      slot.converted = null;
      slot.nat = work.nat;
      slot.upright = work.bitmap;
      slot.bitmap = work.bitmap;
      slot.rotation = 0;
      slot.el.dataset.rotation = '0';
      this.startCrop(slot);
      // Straight into the crop box, which takes the arrow keys.
      if (slot.viewport && slot.state === 'cropping') slot.viewport.focus({ preventScroll: true });
    } catch (error) {
      console.error('[mab-prints] decode failed', error);
      this.showSlotError(slot, notPhotoMessage(file, this.dataset.msgUnsupported, this.dataset.msgDecodeFailed));
    } finally {
      slot.busy = false;
      this.setSlotStatus(slot, '');
      this.updateGate();
    }
  }

  /** The photo itself is too small: said when it is picked. */
  tooSmall(width, height) {
    const template = (this.dataset.msgTooSmall || '').replace('[min]', String(this.minPx));
    return tooSmallMessage(template, width, height);
  }

  /** The photo is fine but the crop is too tight: zoom out, not a new photo. */
  cropTooSmall(width, height) {
    const template = (this.dataset.msgCropTooSmall || '').replace('[min]', String(this.minPx));
    return tooSmallMessage(template, width, height);
  }

  setState(slot, state) {
    slot.state = state;
    slot.el.dataset.state = state;
    if (state !== 'cropping') {
      // The crop box's two canvases hold the viewport at the device's pixel
      // ratio. Six slots of that is real memory on a phone, so a slot that is
      // not cropping hands it back.
      blankCanvas(slot.bleed);
      blankCanvas(slot.crop);
    }
  }

  /** Opens the crop box on the slot's photo: centred, or where it was left. */
  startCrop(slot, options = {}) {
    if (!slot.bitmap || !slot.set.drawn) return;
    this.setState(slot, 'cropping');
    const vw = slot.viewport.clientWidth;
    const vh = slot.viewport.clientHeight;
    if (!vw || !vh) return;
    const bw = slot.bitmap.width;
    const bh = slot.bitmap.height;
    slot.minScale = coverScale(bw, bh, vw, vh);
    if (options.keep && slot.pixels && slot.drawn && slot.drawn.aspect === slot.set.drawn.aspect) {
      // Back to where she left it: the kept region, filling the box again.
      const scale = vw / slot.pixels.w;
      slot.view = clampView({ scale, x: -slot.pixels.x * scale, y: -slot.pixels.y * scale }, bw, bh, vw, vh);
      this.setZoom(slot, Math.max(1, scale / slot.minScale));
    } else {
      slot.view = centredView(bw, bh, vw, vh);
      this.setZoom(slot, 1);
    }
    this.render(slot);
    if (options.keep && slot.applyBtn) slot.applyBtn.focus({ preventScroll: true });
    this.updateGate();
  }

  render(slot) {
    if (!slot.bitmap || slot.state !== 'cropping') return;
    const vw = slot.viewport.clientWidth;
    const vh = slot.viewport.clientHeight;
    if (!vw || !vh) return;
    const dpr = window.devicePixelRatio || 1;
    paintView(slot.bleed, slot.bitmap, slot.view, vw, vh, dpr);
    paintView(slot.crop, slot.bitmap, slot.view, vw, vh, dpr);
  }

  clamp(slot) {
    const vw = slot.viewport.clientWidth;
    const vh = slot.viewport.clientHeight;
    slot.view = clampView(slot.view, slot.bitmap.width, slot.bitmap.height, vw, vh);
  }

  /** The slider's value, and what a screen reader says for it: "150%". */
  setZoom(slot, value) {
    if (!slot.zoom) return;
    slot.zoom.value = String(value);
    this.zoomText(slot);
  }

  zoomText(slot) {
    if (!slot.zoom) return;
    slot.zoom.setAttribute('aria-valuetext', `${Math.round((parseFloat(slot.zoom.value) || 1) * 100)}%`);
  }

  onZoom(slot) {
    this.zoomText(slot);
    if (!slot.bitmap) return;
    const vw = slot.viewport.clientWidth;
    const vh = slot.viewport.clientHeight;
    slot.view = zoomView(slot.view, slot.minScale * (parseFloat(slot.zoom.value) || 1), vw, vh);
    this.clamp(slot);
    this.render(slot);
  }

  /**
   * The crop box by keyboard (WCAG 2.1.1): the arrows move the photo 8 px, 32
   * with Shift, and + or - zoom a tenth, inside the slider's own range. Only
   * those keys are kept from the page; Tab and the rest pass through.
   */
  onViewportKey(slot, event) {
    if (!slot.bitmap || slot.state !== 'cropping') return;
    const step = event.shiftKey ? 32 : 8;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const zooms = { '+': 0.1, '=': 0.1, '-': -0.1 };
    if (moves[event.key]) {
      event.preventDefault();
      this.clearNote(slot);
      const [dx, dy] = moves[event.key];
      slot.view = { ...slot.view, x: slot.view.x + dx, y: slot.view.y + dy };
      this.clamp(slot);
      this.render(slot);
    } else if (zooms[event.key] !== undefined && slot.zoom) {
      event.preventDefault();
      this.clearNote(slot);
      const min = parseFloat(slot.zoom.min) || 1;
      const max = parseFloat(slot.zoom.max) || 4;
      const next = Math.min(max, Math.max(min, (parseFloat(slot.zoom.value) || 1) + zooms[event.key]));
      slot.zoom.value = String(Math.round(next * 100) / 100);
      this.onZoom(slot);
    }
  }

  setupPointer(slot) {
    const el = slot.viewport;
    const pointers = new Map();
    let last = null;
    el.addEventListener('keydown', (e) => this.onViewportKey(slot, e));
    el.addEventListener('pointerdown', (e) => {
      this.clearNote(slot);
      if (!slot.bitmap || slot.state !== 'cropping') return;
      el.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) last = { x: e.clientX, y: e.clientY };
    });
    el.addEventListener('pointermove', (e) => {
      if (!pointers.has(e.pointerId) || !slot.bitmap) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size >= 2) {
        const step = pinchZoom(slot.pinchBase, [...pointers.values()], parseFloat(slot.zoom.value) || 1);
        slot.pinchBase = step.base;
        if (step.zoom !== null) {
          slot.zoom.value = String(step.zoom);
          this.onZoom(slot);
        }
        return;
      }
      if (last) {
        slot.view = { ...slot.view, x: slot.view.x + e.clientX - last.x, y: slot.view.y + e.clientY - last.y };
        last = { x: e.clientX, y: e.clientY };
        this.clamp(slot);
        this.render(slot);
      }
    });
    const end = (e) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) slot.pinchBase = null;
      if (pointers.size === 0) last = null;
      else {
        const p = pointers.values().next().value;
        last = { x: p.x, y: p.y };
      }
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener(
      'wheel',
      (e) => {
        if (!slot.bitmap || slot.state !== 'cropping') return;
        e.preventDefault();
        slot.zoom.value = String(wheelZoom(parseFloat(slot.zoom.value) || 1, e.deltaY));
        this.onZoom(slot);
      },
      { passive: false }
    );
  }

  /**
   * "Use this crop". The kept region is judged in the original's pixels, the
   * ones the print is made from, with the product page's message. What is
   * stored is what _crop needs (the box it was drawn in, nat, the rect as
   * fractions); the _crop string itself is built at send time with the set's
   * locket, so a tile changed to a locket of the same shape still names the
   * right one.
   */
  applyCrop(slot) {
    if (!slot.bitmap || !slot.set.drawn) return;
    const vw = slot.viewport.clientWidth;
    const vh = slot.viewport.clientHeight;
    if (!vw || !vh || !slot.view.scale) return;
    const bw = slot.bitmap.width;
    const bh = slot.bitmap.height;
    const pixels = cropFromView(slot.view, bw, bh, vw, vh);
    // Judged in the original's pixels, in the frame the buyer is looking at:
    // turned a quarter, that frame is nat[1] wide.
    const ratio = rotatedSize(slot.nat[0], slot.nat[1], slot.rotation)[0] / bw;
    if (isTooSmall(pixels.w * ratio, pixels.h * ratio, this.minPx)) {
      this.showSlotError(slot, this.cropTooSmall(pixels.w * ratio, pixels.h * ratio));
      return;
    }
    slot.pixels = pixels;
    slot.rect = normaliseRect(pixels, bw, bh);
    slot.drawn = { shape: slot.set.drawn.shape, aspect: slot.set.drawn.aspect };
    this.hideSlotError(slot);
    this.setState(slot, 'done');
    this.drawPair(slot);
    this.updateGate();
    const adjust = slot.el.querySelector('[data-act="adjust"]');
    if (adjust) adjust.focus({ preventScroll: true });
  }

  /**
   * Rotate left or right (spec criterion 37): a quarter turn of the crop box
   * and both prints, never of the File, which goes on the line as it came.
   * The crop box redraws a turned copy of the upright working copy, and the
   * crop resets to the opening crop for the new frame: centred, covering the
   * shape. In the crop box that is where the view opens; with the pair
   * showing, the opening crop is placed straight away and both prints are
   * redrawn, unless that crop is too small to print, in which case the crop
   * box opens with the reason.
   */
  async rotate(slot, direction) {
    if (!slot.upright || slot.busy || !slot.set.drawn) return;
    slot.busy = true;
    try {
      const next = rotateStep(slot.rotation, direction);
      const turned = await rotatedCopy(slot.upright, next);
      if (!turned) return;
      if (slot.bitmap && slot.bitmap !== slot.upright && slot.bitmap !== turned) release(slot.bitmap);
      slot.bitmap = turned;
      slot.rotation = next;
      slot.el.dataset.rotation = String(next);
      slot.pixels = null;
      this.hideSlotError(slot);
      if (slot.state === 'done') {
        const pixels = defaultCrop(turned.width, turned.height, slot.set.drawn.aspect);
        const ratio = rotatedSize(slot.nat[0], slot.nat[1], next)[0] / turned.width;
        if (isTooSmall(pixels.w * ratio, pixels.h * ratio, this.minPx)) {
          this.startCrop(slot);
          this.showSlotError(slot, this.cropTooSmall(pixels.w * ratio, pixels.h * ratio));
        } else {
          slot.pixels = pixels;
          slot.rect = normaliseRect(pixels, turned.width, turned.height);
          slot.drawn = { shape: slot.set.drawn.shape, aspect: slot.set.drawn.aspect };
          this.drawPair(slot);
        }
      } else {
        this.startCrop(slot);
      }
      // Said, because the crop she placed is gone and nothing else says so.
      this.setNote(slot, (this.dataset.msgRotated || '').replace('[photo]', this.slotName(slot)));
    } finally {
      slot.busy = false;
      this.updateGate();
    }
  }

  /**
   * The two prints, side by side: the same crop of the same photo drawn into
   * two canvases, one of which CSS shows in black and white. Previews only.
   * Neither canvas is ever read back, and the File on the line is the slot's
   * original.
   */
  drawPair(slot) {
    if (slot.state !== 'done' || !slot.bitmap || !slot.pixels) return;
    const { x, y, w, h } = slot.pixels;
    const dpr = window.devicePixelRatio || 1;
    [slot.colour, slot.grey].forEach((canvas) => {
      if (!canvas) return;
      const box = canvas.parentElement;
      const pw = box.clientWidth;
      const ph = box.clientHeight;
      if (!pw || !ph) return;
      canvas.width = Math.round(pw * dpr);
      canvas.height = Math.round(ph * dpr);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, pw, ph);
      ctx.drawImage(slot.bitmap, x, y, w, h, 0, 0, pw, ph);
    });
  }

  clearSlot(slot) {
    if (slot.bitmap && slot.bitmap !== slot.upright) release(slot.bitmap);
    release(slot.upright);
    slot.upright = null;
    slot.bitmap = null;
    slot.rotation = 0;
    slot.el.dataset.rotation = '0';
    slot.original = null;
    slot.converted = null;
    slot.nat = null;
    slot.rect = null;
    slot.pixels = null;
    slot.drawn = null;
    slot.view = { scale: 0, x: 0, y: 0 };
    blankCanvas(slot.colour);
    blankCanvas(slot.grey);
    this.hideSlotError(slot);
    this.clearNote(slot);
    this.setState(slot, 'empty');
  }

  redrawAll() {
    this.active.forEach((set) =>
      set.slots.forEach((slot) => {
        if (slot.state === 'cropping' && slot.bitmap) {
          const vw = slot.viewport.clientWidth;
          const vh = slot.viewport.clientHeight;
          if (!vw || !vh) return;
          slot.minScale = coverScale(slot.bitmap.width, slot.bitmap.height, vw, vh);
          // A wider box can leave the photo short of an edge; hold it at the
          // smallest scale that still covers the box.
          if (slot.view.scale < slot.minScale) slot.view = zoomView(slot.view, slot.minScale, vw, vh);
          this.clamp(slot);
          this.render(slot);
        } else if (slot.state === 'done') {
          this.drawPair(slot);
        }
      })
    );
  }

  /*
   * Errors and status lines are always in the page, and empty is how they are
   * hidden (the CSS draws the box only when there are words in it). A live
   * region that is unhidden and filled in one go is often not read out; one
   * that was there all along and has its text changed is.
   */
  showSlotError(slot, message) {
    this.setStatus(slot.error, message);
  }

  hideSlotError(slot) {
    this.setStatus(slot.error, '');
  }

  setSlotStatus(slot, message) {
    slot.note = false;
    this.setStatus(slot.status, message);
  }

  /** A status line that the next thing done to the photo takes away. */
  setNote(slot, message) {
    this.setSlotStatus(slot, message);
    slot.note = !!message;
  }

  clearNote(slot) {
    if (slot.note) this.setSlotStatus(slot, '');
  }

  /** "Photo 2", as the slot's own label says it. */
  slotName(slot) {
    const label = slot.el.querySelector('[data-slot-name]');
    return label ? label.textContent.trim() : '';
  }

  /* ---- the receipt ------------------------------------------------------- */

  onReceipt() {
    const file = this.receiptInput.files && this.receiptInput.files[0];
    this.receiptInput.value = '';
    if (!file) return;
    if (!isReceiptFile(file)) {
      this.showReceiptError(this.dataset.msgReceiptUnsupported);
      return;
    }
    this.setReceipt(file);
    // The pick button has just gone; focus goes to its stand-in, Replace.
    const replace = this.querySelector('.mab-prints__receipt-set [data-act="receipt-pick"]');
    if (replace) replace.focus();
  }

  setReceipt(file) {
    this.receipt = file || null;
    this.showReceiptError('');
    if (this.receiptBox) this.receiptBox.dataset.state = file ? 'set' : 'empty';
    if (this.receiptName) this.receiptName.textContent = file ? file.name || '' : '';
    this.updateGate();
  }

  showReceiptError(message) {
    this.setStatus(this.receiptError, message);
  }

  /* ---- the gate and the button ------------------------------------------- */

  gateInput() {
    return {
      sets: this.active.map((set) => ({
        locket: this.offered.includes(set.locket) && !!variantFor(this.variants, this.booth, set.locket),
        // Placed, and with a whole print choice: a radio is checked from the
        // start, but the gate reads it rather than assuming it.
        photos: set.slots.map((slot) => slot.state === 'done' && !!this.mixFor(slot)),
      })),
      rights: !!(this.rights && this.rights.checked),
      receipt: !!this.receipt,
      // Only "Buy photos" goes without: the early press never names the
      // receipt there, and a receipt kept from the kit path is not counted.
      receiptRequired: this.boothKind() !== BOOTH_BUY,
    };
  }

  missingMessage(missing) {
    if (!missing) return '';
    const d = this.dataset;
    return { locket: d.msgNeedLocket, photo: d.msgNeedPhotos, rights: d.msgNeedRights, receipt: d.msgNeedReceipt }[missing.what] || '';
  }

  updateGate() {
    if (!this.submitBtn) return;
    const missing = firstMissing(this.gateInput());
    const gated = !!missing || !!this._sending;
    // The product page's pattern: visually gated with aria-disabled, never the
    // real disabled, so a press is still heard and can say what is missing.
    this.submitBtn.classList.toggle('mab-prints__submit--gated', gated);
    if (gated) this.submitBtn.setAttribute('aria-disabled', 'true');
    else this.submitBtn.removeAttribute('aria-disabled');
    const label = this.submitBtn.querySelector('[data-submit-label]');
    if (label) {
      label.textContent = buttonLabel({
        booth: this.booth,
        kitValue: this.kitValue,
        cents: totalCents(this.variants, this.booth, this.active.map((set) => set.locket)),
        currency: this.dataset.currency,
        locale: this.dataset.locale,
        sendLabel: this.dataset.sendLabel,
        checkoutLabel: this.dataset.checkoutLabel,
      });
    }
    this.setStatus(this.helper, this._sending ? '' : this.missingMessage(missing));
  }

  focusFirstTile(set) {
    const target = set.tiles.find((t) => t.checked) || set.tiles[0];
    if (!target) return;
    target.focus({ preventScroll: true });
    target.closest('fieldset').scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'center' });
  }

  /** Moves focus to the first missing thing, and scrolls it into view. */
  focusMissing(missing) {
    let target = null;
    if (missing.what === 'locket') {
      this.focusFirstTile(this.active[missing.set]);
      return;
    }
    if (missing.what === 'photo') {
      const slot = this.active[missing.set].slots[missing.slot];
      // A placed photo with no print choice goes to the choice.
      if (slot.state === 'cropping') target = slot.applyBtn;
      else if (slot.state === 'done') target = slot.mixInputs[0] || null;
      else target = slot.pickBtn;
    } else if (missing.what === 'rights') {
      target = this.rights;
    } else if (missing.what === 'receipt') {
      target = this.querySelector('[data-act="receipt-pick"]');
    }
    if (!target) return;
    target.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'center' });
    target.focus({ preventScroll: true });
  }

  onSubmit() {
    if (this._sending) return;
    const missing = firstMissing(this.gateInput());
    if (missing) {
      this.updateGate();
      this.focusMissing(missing);
      return;
    }
    this.send();
  }

  /**
   * One set's line, ready for submitSets, taken as it stands at the press.
   *
   * Everything the line needs is copied here, before the first request: the
   * variant, the booth path, the locket's handle and shape, each photo's File,
   * its _crop string and its print choice, and on the kit path the receipt. A
   * set is built lazily, one after the other, so anything read later could
   * change under an upload in progress (a photo removed, a tile changed). The
   * _crop strings are built now, so a crop that cannot be built throws here,
   * before anything is sent. The HEIC resend copies are cached on this
   * snapshot, never on the slot.
   *
   * `booth` is the option value at the press and `receipt` the snapshot taken
   * with it (null on "Buy photos"); send() reads both once, for every set, so
   * a switch after the press cannot move a line to the other path.
   * `receipt` is shared by every set in one submission, so its resend copy is
   * made once.
   */
  setPayload(set, booth, receipt) {
    const choice = boothChoice(booth, this.kitValue);
    if (!choice) throw new Error('no booth choice');
    const kit = choice === BOOTH_KIT;
    if (kit && !(receipt && receipt.file)) throw new Error('a kit set has no receipt');
    const variant = variantFor(this.variants, booth, set.locket);
    if (!variant) throw new Error('a set has no variant');
    const handle = set.handle;
    const shape = set.shape;
    const photos = set.slots.map((slot) => ({ original: slot.original, nat: slot.nat ? [slot.nat[0], slot.nat[1]] : null, converted: null }));
    const crops = set.slots.map((slot) => cropPayload(slot, handle));
    // Copied, not referenced: a radio changed while the photos send cannot
    // change a count on a line that has not gone up yet.
    const mixes = set.slots.map((slot) => {
      const mix = this.mixFor(slot);
      if (!mix) throw new Error('a photo has no print choice');
      return { colour: mix.colour, bw: mix.bw };
    });
    const heic = photos.some((p) => isHeicFile(p.original)) || (kit && isHeicFile(receipt.file));
    return {
      heic,
      build: async (converted) => {
        const files = [];
        const sources = { photos: photos.map(() => false), receipt: false };
        for (let i = 0; i < photos.length; i += 1) {
          const photo = photos[i];
          if (converted && isHeicFile(photo.original)) {
            const copy = await this.convertPhoto(photo);
            if (!copy) return null;
            files.push(copy);
            sources.photos[i] = true;
          } else {
            files.push(photo.original);
          }
        }
        // "Buy photos" has no receipt: none is read, converted or sent.
        let receiptFile = kit ? receipt.file : null;
        if (kit && converted && isHeicFile(receipt.file)) {
          receiptFile = await this.convertReceipt(receipt);
          if (!receiptFile) return null;
          sources.receipt = true;
        }
        return lineEntries({
          variantId: variant.id,
          photos: files,
          crops,
          mixes,
          shape,
          boothChoice: choice,
          receipt: receiptFile,
          sources,
        });
      },
    };
  }

  /**
   * The receipt as it stands at the press, with room for its resend copy, on
   * the kit path; null on "Buy photos", where a receipt kept from the kit
   * path stays on the page and off the order.
   */
  receiptSnapshot(choice) {
    return choice === BOOTH_KIT ? { file: this.receipt, converted: null } : null;
  }

  /**
   * The resend copy of a refused HEIC photo: the core's full-resolution JPEG,
   * refused in turn if it is not the upright size the crop was recorded on.
   * UNTESTED against a real HEIC, like the product page's: no HEIC encoder was
   * available to make a fixture.
   *
   * `photo` is a payload's snapshot, { original, nat, converted }.
   */
  async convertPhoto(photo) {
    if (photo.converted) return photo.converted;
    const out = await fullResJpeg(photo.original, this.dataset.heicLib);
    if (!matchesNat(out, photo.nat)) return null;
    const base = (photo.original.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
    photo.converted = new File([out.blob], `${base}.jpg`, { type: 'image/jpeg' });
    return photo.converted;
  }

  /** `receipt` is a submission's snapshot, { file, converted }. */
  async convertReceipt(receipt) {
    if (receipt.converted) return receipt.converted;
    const out = await fullResJpeg(receipt.file, this.dataset.heicLib);
    if (!out) return null;
    const base = (receipt.file.name || 'receipt').replace(/\.[^.]+$/, '') || 'receipt';
    receipt.converted = new File([out.blob], `${base}.jpg`, { type: 'image/jpeg' });
    return receipt.converted;
  }

  /**
   * While a submission is sending, nothing on the page can be changed: the
   * booth choice and every step under it go inert. The step holding the
   * button and the "Sending" status stays out of it, because inert would take
   * the status out of what a screen reader hears and drop focus from the
   * button to the page. The button is gated by _sending meanwhile.
   */
  lockables() {
    const out = [];
    const booth = this.querySelector('[data-booth]');
    if (booth) out.push(booth);
    if (this.rest) out.push(...Array.from(this.rest.children).filter((el) => !el.hasAttribute('data-send')));
    return out;
  }

  setSending(on) {
    this._sending = on;
    this.lockables().forEach((el) => el.toggleAttribute('inert', on));
    // Letting go lifts inert from every step, the receipt's included; on
    // "Buy photos" it goes straight back.
    this.syncReceiptStep();
    this.setStatus(this.sendStatus, on ? this.dataset.msgSending : '');
    this.updateGate();
  }

  /** Text into an always-present live region; empty is how it is hidden. */
  setStatus(el, message) {
    if (el) el.textContent = message || '';
  }

  /** Every variant under a booth choice (the current one by default), for the stale-line clear. */
  boothVariantIds(booth = this.booth) {
    return this.variants.filter((v) => v && Array.isArray(v.options) && v.options[0] === booth).map((v) => v.id);
  }

  async send() {
    this.setStatus(this.sendError, '');
    let result = { ok: false };
    let started = false;
    try {
      // The booth choice and the receipt are read once, at the press, and
      // every set, the receipt and the clear use that reading.
      const booth = this.booth;
      const receipt = this.receiptSnapshot(boothChoice(booth, this.kitValue));
      // The payloads are taken before setSending and before the first request,
      // so what goes up is what the buyer saw when she pressed.
      const request = {
        sets: this.active.map((set) => this.setPayload(set, booth, receipt)),
        productHandle: this.productHandle,
        urls: this.urls,
        fetch: (...args) => window.fetch(...args),
        clearVariantIds: this.boothVariantIds(booth),
      };
      this.setSending(true);
      started = true;
      result = await submitSets(request);
      if (result.error) console.error('[mab-prints] send failed', result.error);
    } catch (error) {
      console.error('[mab-prints] send failed', error);
      result = { ok: false };
    }
    if (result.ok && result.unverified) {
      // The cart could not be checked after a lost response, so the buyer
      // sees her lines on the cart page before she pays, not at checkout.
      window.location.assign(this.dataset.cartUrl || '/cart');
      return;
    }
    if (result.ok) {
      // Straight to checkout. The cart drawer is never opened and no cart
      // event is raised, so nothing on the page redraws under the buyer.
      window.location.assign(this.dataset.checkoutUrl || '/checkout');
      return;
    }
    if (started) this.setSending(false);
    // When the rollback itself could not be made, prints lines may be left
    // in the cart, and the message says so and what to do about it.
    const message = result.rolledBack === false ? this.dataset.msgUploadPartial : this.dataset.msgUploadFailed;
    this.setStatus(this.sendError, message);
    this.updateGate();
  }

  /**
   * Back from checkout through the browser's back button, the page can come
   * back from the back/forward cache exactly as it was left: sending, and
   * locked. It is put back to usable.
   */
  onPageShow(event) {
    if (!event || !event.persisted || !this._sending) return;
    this.setSending(false);
  }
}

// Exported so the check can run a method against a stand-in host.
export { MabLocketPrints };

if (typeof customElements !== 'undefined' && !customElements.get('mab-locket-prints')) {
  customElements.define('mab-locket-prints', MabLocketPrints);
}
